import React, {useCallback, useEffect, useState} from "react";
import {ModalAttachmentsProps} from "./ModalAttachments";
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import TextLabel from "src/Controls/TextLabel.mjs";
import "./ModalAttachments.scss";
import {Attachment} from "src/interfaces/emails";
import {useDropzone} from "react-dropzone";
import AttachIcon from "../../../../../../media/images/AttachIcon.mjs";
import AttachmentList from "../AttachmentList.mjs";
import CloseIcon from "../../../../../../media/images/CloseIcon.mjs";

const ModalAttachments: React.FC<ModalAttachmentsProps> = ({
															   open,
															   setOpen,
															   attachments,
															   savedAttachments,
															   setSavedAttachments,
														   }) => {
	const toggle = () => setOpen(!open);
	const [selectedAttachments, setSelectedAttachments] =
		useState<Attachment[]>();

	useEffect(() => {
		if (savedAttachments) setSelectedAttachments(savedAttachments);
	}, [savedAttachments]);

	useEffect(() => {
		// @ts-ignore
		setSavedAttachments(selectedAttachments);
	}, [selectedAttachments]);

	const handleSelectAttachment = (attachment: Attachment): void => {
		setSelectedAttachments((prevState = []) => {
			const isSelected = prevState.some((a) => a.name === attachment.name);
			if (isSelected) {
				return prevState.filter((a) => a.name !== attachment.name);
			} else {
				return [...prevState, attachment];
			}
		});
	};

	function handleAttachmentsSave(): void {
		// @ts-ignore
		setSavedAttachments(selectedAttachments);
		setOpen(false);
	}

	function handleDownload(attachment: Attachment): void {
		// eslint-disable-next-line no-restricted-globals
		event?.stopPropagation();
		const base64Data = (attachment?.content||"").split(";base64,").pop();
		const blobData = base64Data ? atob(base64Data) : "";
		const byteNumbers = new Array(blobData.length);
		for (let i = 0; i < blobData.length; i++) {
			byteNumbers[i] = blobData.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);

		const blob = new Blob([byteArray], {type: attachment.type});

		const blobUrl = URL.createObjectURL(blob);

		const downloadLink = document.createElement("a");
		downloadLink.href = blobUrl;
		downloadLink.download = attachment.name;
		document.body.appendChild(downloadLink);
		downloadLink.click();

		document.body.removeChild(downloadLink);
		URL.revokeObjectURL(blobUrl);
	}

	const onDrop = useCallback((acceptedFiles: File[]) => {
		const newAttachments = acceptedFiles.map((file) => {
			return new Promise<Attachment>((resolve) => {
				const reader = new FileReader();

				reader.onload = (e) => {
					resolve({
						name: file.name,
						type: file.type,
						content: e.target?.result as string,
						size: file.size,
					});
				};

				reader.readAsDataURL(file);
			});
		});

		Promise.all(newAttachments).then((attachments: any) => {
			setSelectedAttachments((prevState) => [
				...(prevState || []),
				...attachments,
			]);
		});
	}, []);

	const {getRootProps, getInputProps, isDragActive} = useDropzone({
		onDrop,
		accept: {
			"text/plain": [".txt"],
			"text/rtf": [".rtf"],
			"text/xml": [".xml"],
			"text/html": [".htm", ".html"],
			"application/pdf": [".pdf"],
			"application/msword": [".doc"],
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
			"application/vnd.ms-excel": [".xls", ".xlsx"],
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
			"application/vnd.ms-powerpoint": [".ppt", ".pps"],
			"application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
			"application/vnd.openxmlformats-officedocument.presentationml.slideshow": [".ppsx"],
			"application/octet-stream": [".eml"],
			"application/x-rar-compressed": [".rar"],
			"application/zip": [".zip"],
			"audio/aac": [".aac"],
			"audio/mpeg":[".mp3"],
			"audio/wav":[".wav"],
			"audio/webm":[".weba"],
			"audio/3gpp":[".3gp"],
			"video/x-msvideo":[".avi"],
			"video/mp4":[".mp4"],
			"video/mpeg":[".mpeg",".mpg"],
			"video/webm":[".webm"],
			"video/3gpp":[".3gp"],
			"image/jpeg":[".jpg",".jpeg"]
		},
		maxSize: 10240000
	});

	return (
		<Modal className="modal__attachments" isOpen={open} toggle={toggle}>
			<ModalHeader toggle={toggle}>
				<AttachIcon/>
				<TextLabel value={"Attachments"}/>
			</ModalHeader>
			<ModalBody>
				<AttachmentList
					fullWidth={true}
					attachments={selectedAttachments}
					size={true}
					onDownload={handleDownload}
					onDelete={handleSelectAttachment}
				/>
				<div className="dropzone" {...getRootProps()}>
					<input {...getInputProps()} />
					<span className={"nomobile"}>
						<TextLabel value={"AttachDropArea"}/>
					</span>
					<span className={"mobileonly"}>
						<TextLabel value={"AttachClickArea"} />
					</span>
				</div>
			</ModalBody>
			<ModalFooter>
				<Button
					className={"btn-icon"}
					onClick={ ()=>{ setOpen(false); }}
				>
					<b>
						<TextLabel value={"Close"}/>
					</b>
					<i>
						<CloseIcon />
					</i>
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default ModalAttachments;
